import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import Particles from "./Particles";
import { ArrowDown, MapPin, Phone } from "lucide-react";
import { BorderOrnament, DiyaDots } from "./IndianMotifs";

const HEADLINE_WORDS = [
  { text: "Care", tone: "ink" },
  { text: "for", tone: "ink" },
  { text: "your", tone: "ink" },
  { text: "smile.", tone: "ink" },
  { text: "Folded", tone: "script" },
  { text: "with", tone: "ink" },
  { text: "care.", tone: "ink" },
];

export default function Hero({ onBook }) {
  const root = useRef(null);
  const title = useRef(null);
  const subtitle = useRef(null);
  const meta = useRef(null);
  const cta = useRef(null);
  const layer1 = useRef(null);
  const layer2 = useRef(null);
  const layer3 = useRef(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ctx = gsap.context(() => {
      if (prefersReducedMotion) return; // WCAG 2.3.3: Skip all animations
      // Letter-by-letter reveal
      const letters = title.current?.querySelectorAll(".letter") || [];
      gsap.set(letters, { yPercent: 110, opacity: 0, rotateX: -45 });
      gsap.set([subtitle.current, meta.current, cta.current], { opacity: 0, y: 30 });
      gsap.set([layer1.current, layer2.current, layer3.current], { opacity: 0, y: 60, scale: 1.04 });

      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl.to([layer1.current, layer2.current, layer3.current], {
        opacity: 1, y: 0, scale: 1, duration: 1.4, stagger: 0.12,
      }, 0.1)
        .to(letters, { yPercent: 0, opacity: 1, rotateX: 0, duration: 0.9, stagger: 0.022, ease: "expo.out" }, 0.4)
        .to(subtitle.current, { opacity: 1, y: 0, duration: 1 }, "-=0.5")
        .to(meta.current, { opacity: 1, y: 0, duration: 0.8 }, "-=0.6")
        .to(cta.current, { opacity: 1, y: 0, duration: 0.8 }, "-=0.5");

      // Parallax on mouse (skip if reduced motion)
      const onMove = (e) => {
        if (prefersReducedMotion) return;
        const cx = (e.clientX / window.innerWidth - 0.5) * 2;
        const cy = (e.clientY / window.innerHeight - 0.5) * 2;
        gsap.to(layer1.current, { x: cx * 22, y: cy * 14, duration: 1.2, ease: "power3.out" });
        gsap.to(layer2.current, { x: cx * -18, y: cy * -10, duration: 1.2, ease: "power3.out" });
        gsap.to(layer3.current, { x: cx * 10, y: cy * 6, duration: 1.2, ease: "power3.out" });
        gsap.to(title.current, { x: cx * 6, y: cy * 4, duration: 1.4, ease: "power3.out" });
      };
      window.addEventListener("mousemove", onMove);
      return () => window.removeEventListener("mousemove", onMove);
    }, root);
    return () => ctx.revert();
  }, []);

  const renderHeadline = () =>
    HEADLINE_WORDS.map((w, idx) => (
      <React.Fragment key={idx}>
        <span
          className={`inline-block align-baseline whitespace-nowrap ${
            w.tone === "script" ? "font-script text-gold-500 px-2" : ""
          }`}
        >
          {[...w.text].map((ch, i) => (
            <span key={i} className="letter inline-block">
              {ch}
            </span>
          ))}
        </span>
        {idx < HEADLINE_WORDS.length - 1 && <span className="inline-block">{"\u00A0"}</span>}
      </React.Fragment>
    ));

  return (
    <section
      id="hero"
      ref={root}
      aria-label="Hero — Dentist"
      className="relative mesh-bg grain overflow-hidden min-h-[100svh] flex items-center pt-28 md:pt-32"
    >
      <Particles />

      {/* Decorative parallax layers */}
      <div ref={layer1} className="absolute -left-24 top-24 w-[360px] h-[360px] rounded-full bg-gold-200/40 blur-3xl" />
      <div ref={layer2} className="absolute right-[-120px] top-[10%] w-[420px] h-[420px] rounded-full bg-sage-200/50 blur-3xl" />
      <div ref={layer3} className="absolute left-[40%] bottom-[-180px] w-[520px] h-[520px] rounded-full bg-[rgba(244,213,213,0.5)] blur-3xl" />

      <div className="container mx-auto px-5 md:px-10 relative z-10">
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 lg:col-span-8 xl:col-span-9">
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-sage-700/90 mb-6 reveal in" data-testid="hero-eyebrow">
              <span className="inline-block w-10 h-px bg-sage-700/60" />
              Dentist
              <DiyaDots count={5} className="text-gold-500/80 ml-1" />
            </div>

            <h1
              ref={title}
              data-testid="hero-headline"
              className="font-display text-[14vw] sm:text-[11vw] lg:text-[8vw] xl:text-[6.6vw] leading-[0.95] tracking-tightest text-ink"
              style={{ perspective: 600 }}
            >
              {renderHeadline()}
            </h1>
            <div className="mt-6 flex items-center gap-4 text-gold-600/80">
              <BorderOrnament width={220} />
            </div>

            <p ref={subtitle} data-testid="hero-subtitle" className="mt-7 max-w-xl text-ink/70 text-lg leading-relaxed">
              Friendly, gentle, local dentistry. Every visit at Dentist is shaped with the patience of a single, perfect fold — comfort, confidence, and a smile you'll want to share.
            </p>

            <div ref={cta} className="mt-10 flex flex-wrap items-center gap-4">
              <button
                onClick={onBook}
                data-testid="hero-book-btn"
                data-cursor="hover"
                className="group relative inline-flex items-center gap-3 rounded-full bg-sage-700 text-cream pl-7 pr-2 py-2 btn-glow tilt-card"
              >
                <span className="uppercase text-[12px] tracking-[0.28em]">Book a Consultation</span>
                <span className="grid place-items-center w-11 h-11 rounded-full bg-gold-400 text-sage-900 group-hover:rotate-45 transition-transform duration-500">
                  <ArrowDown className="-rotate-45 w-4 h-4" strokeWidth={2.2} />
                </span>
              </button>
              <a
                href="https://maps.google.com/?q=123+Dental+St,+Cityville"
                target="_blank" rel="noreferrer"
                data-testid="hero-directions"
                data-cursor="hover"
                className="inline-flex items-center gap-2 text-sm text-sage-800/90 underline-grow"
              >
                <MapPin className="w-4 h-4" /> 123 Dental St, Cityville
              </a>
            </div>
          </div>

          <div ref={meta} className="col-span-12 lg:col-span-4 xl:col-span-3 lg:pl-8">
            <div className="space-y-6 lg:border-l lg:border-sage-700/15 lg:pl-8">
              <div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-sage-700/70">Open Mon-Sat</div>
                <div className="font-display text-2xl text-sage-900 mt-1">Same-Day Emergency</div>
              </div>
              <div className="divider-script" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-sage-700/70">Direct Line</div>
                <a href="tel:+15551234567" data-cursor="hover" className="font-display text-2xl text-sage-900 mt-1 flex items-center gap-2 underline-grow">
                  <Phone className="w-4 h-4 text-gold-500" /> +1 (555) 123-4567
                </a>
              </div>
              <div className="divider-script" />
              <div className="text-xs font-semibold uppercase tracking-widest text-ink/70 space-y-2">
                <div>✦ 20+ Years Caring</div>
                <div>✦ 5★ Patient Rated</div>
                <div>✦ 100% Direct Billing</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="mt-16 md:mt-24 flex items-center justify-between text-[10px] uppercase tracking-[0.36em] text-sage-700/70">
          <span>Cityville - Downtown Area</span>
          <span className="flex items-center gap-3">
            Scroll <span className="inline-block w-12 h-px bg-sage-700/40" />
            <ArrowDown className="w-3.5 h-3.5 animate-bounce" />
          </span>
          <span className="hidden md:inline">@dentist</span>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 overflow-hidden bg-sage-900/5 backdrop-blur-sm border-t border-sage-900/10 py-3">
        <div className="marquee-track text-[10px] uppercase tracking-[0.3em] text-sage-900/80 font-bold">
          <span className="px-6">✦ GENTLE CARE</span>
          <span className="px-6">✦ MODERN TECHNOLOGY</span>
          <span className="px-6">✦ FAMILY DENTISTRY</span>
          <span className="px-6">✦ SAME-DAY EMERGENCY</span>
          <span className="px-6">✦ DIRECT INSURANCE BILLING</span>
          <span className="px-6">✦ BILINGUAL TEAM</span>
          <span className="px-6">✦ GENTLE CARE</span>
          <span className="px-6">✦ MODERN TECHNOLOGY</span>
          <span className="px-6">✦ FAMILY DENTISTRY</span>
          <span className="px-6">✦ SAME-DAY EMERGENCY</span>
          <span className="px-6">✦ DIRECT INSURANCE BILLING</span>
          <span className="px-6">✦ BILINGUAL TEAM</span>
        </div>
      </div>
    </section>
  );
}
